<template>
    <div
        class="RequirementTypeList__content"
        :class="{ 'RequirementTypeList__content--empty': requirementTypeList.length === 0 }"
    >
        <div v-if="requirementTypeList.length === 0" class="RequirementTypeList__emptyState">
            <div class="RequirementTypeList__emptyStateIcon">
                <FontAwesomeIcon :icon="faClipboardList" />
            </div>
            <h3 class="RequirementTypeList__emptyStateTitle">
                Nothing Added Yet
            </h3>
            <p class="RequirementTypeList__emptyStateDescription">
                Start building your risk profile by adding requirements. Use the "Add Requirement" button above to get started.
            </p>
            <p class="RequirementTypeList__emptyStateHint">
                You can add requirements such as insurance coverages, licenses, certifications, and other compliance criteria.
            </p>
        </div>
        <div class="flex flex-col gap-[14px]">
            <RiskRequirement
                v-for="requirementType of sortedRequirementTypeList"
                :key="requirementType"
                :has-deprecated-criteria="hasDeprecatedCriteriaByRequirementType(requirementType)"
                :has-invalid-references="isReferenceStartsWithIsInvalid(requirementType)"
                :requirement-type="requirementType"
                :title="getRequirementTypeLabel(requirementType)"
                :criteria-count="getCriteriaCount(criteriaByType, requirementType)"
                :expanded="expandedRequirementTypes.includes(requirementType)"
                :is-requirement-type-in-evaluation-rule="isRequirementTypeInEvaluationRule(requirementType)"
                :criteria-list="getCriteriaList(criteriaByType, requirementType)"
                :templates-by-field-name="getTemplatesByFieldName(templatesByRequirementType, requirementType)"
                :custom-properties="customProperties"
                :is-reference-starts-with-is-invalid="isReferenceStartsWithIsInvalid"
                :country-code="countryCode"
                :criteria-templates="getCriteriaTemplates(criteriaTemplatesByRequirementType, requirementType)"
                :evaluation-rule-requirement-types="evaluationRuleRequirementTypes"
                :show-validation-errors="showValidationErrors"
                :can-delete-requirement-type="canDeleteRequirementType"
                :can-manage-criteria="canManageCriteria"
                :can-open-criterion-settings="canOpenCriterionSettings"
                @delete="$emit('delete', requirementType)"
                @update:expanded="handleExpansionChange(requirementType, $event)"
                @criteria-input="$emit('criteriaInput', { criteria: $event, requirementType })"
                @reference-accessed="$emit('reference-accessed', $event)"
                @validity-change="$emit('validityChange', $event)"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import sortBy from 'lodash/sortBy';

    import { faClipboardList } from '@fortawesome/pro-regular-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import RiskRequirement from '@/modules/decisioning-criteria/components/RiskRequirement/RiskRequirement.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';

    type HasDeprecatedCriteriaByRequirementType = (requirementType: TprmRequirementType) => boolean;
    type GetRequirementTypeLabel = (requirementType: TprmRequirementType) => string;
    type IsRequirementTypeInEvaluationRule = (requirementType: TprmRequirementType) => boolean;
    type IsReferenceStartsWithIsInvalid = (searchString: string) => boolean;

    type CriteriaByType = Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>>;
    type TemplatesByRequirementType = Partial<Record<TprmRequirementType, Record<string, CriterionTemplate>>>;
    type CriteriaTemplatesByRequirementType = Partial<Record<TprmRequirementType, CriterionTemplate[]>>;

    export default defineComponent({
        name: 'RequirementsEditorList',
        components: {
            FontAwesomeIcon,
            RiskRequirement,
        },
        props: {
            criteriaByType: {
                type: Object as PropType<CriteriaByType>,
                default: (): CriteriaByType => ({}),
            },
            expandedRequirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            hasDeprecatedCriteriaByRequirementType: {
                type: Function as PropType<HasDeprecatedCriteriaByRequirementType>,
                required: true,
            },
            getRequirementTypeLabel: {
                type: Function as PropType<GetRequirementTypeLabel>,
                required: true,
            },
            isRequirementTypeInEvaluationRule: {
                type: Function as PropType<IsRequirementTypeInEvaluationRule>,
                required: true,
            },
            isReferenceStartsWithIsInvalid: {
                type: Function as PropType<IsReferenceStartsWithIsInvalid>,
                required: true,
            },
            templatesByRequirementType: {
                type: Object as PropType<
                    TemplatesByRequirementType
                >,
                default: (): TemplatesByRequirementType => ({}),
            },
            criteriaTemplatesByRequirementType: {
                type: Object as PropType<CriteriaTemplatesByRequirementType>,
                default: (): CriteriaTemplatesByRequirementType => ({}),
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [],
            },
            countryCode: {
                type: [ String, null ] as PropType<string | null | undefined>,
                default: undefined,
            },
            evaluationRuleRequirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            showValidationErrors: {
                type: Boolean,
                default: false,
            },
            canDeleteRequirementType: {
                type: Boolean,
                default: true,
            },
            canManageCriteria: {
                type: Boolean,
                default: true,
            },
            canOpenCriterionSettings: {
                type: Boolean,
                default: true,
            },
        },
        emits: [ 'criteriaInput', 'delete', 'expand', 'reference-accessed', 'validityChange' ],
        data() {
            return {
                faClipboardList,
            };
        },
        computed: {
            typedCriteriaByType(): CriteriaByType {
                return this.criteriaByType as Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>>;
            },
            requirementTypeList(): TprmRequirementType[] {
                return Object.keys(this.typedCriteriaByType) as TprmRequirementType[];
            },
            sortedRequirementTypeList(): TprmRequirementType[] {
                return sortBy([ ...this.requirementTypeList ], this.getRequirementTypeLabel);
            },
        },
        methods: {
            getCriteriaCount(criteriaByType: CriteriaByType, type: TprmRequirementType): number {
                return this.getCriteriaList(criteriaByType, type).length;
            },
            getCriteriaList(
                criteriaByType: CriteriaByType,
                type: TprmRequirementType,
            ): EditableRequirementCriterion[] {
                return criteriaByType[type] || [];
            },
            getTemplatesByFieldName(
                templatesByRequirementType: TemplatesByRequirementType,
                type: TprmRequirementType,
            ): Record<string, CriterionTemplate> {
                return templatesByRequirementType[type] || {};
            },
            getCriteriaTemplates(
                criteriaTemplatesByRequirementType: CriteriaTemplatesByRequirementType,
                type: TprmRequirementType,
            ): CriterionTemplate[] {
                return criteriaTemplatesByRequirementType[type] || [];
            },
            handleExpansionChange(requirementType: TprmRequirementType, expanded: boolean): void {
                if (expanded) {
                    if (!this.expandedRequirementTypes.includes(requirementType)) {
                        this.$emit('expand', [ ...this.expandedRequirementTypes, requirementType ]);
                    }
                    return;
                }

                this.$emit(
                    'expand',
                    this.expandedRequirementTypes.filter((type) => type !== requirementType),
                );
            },
        },
    });
</script>
